import { HttpClient } from '@angular/common/http';

import { Injectable, inject } from '@angular/core';

import { Observable, from, of } from 'rxjs';

import {
  AUDIT_LOG_API,
  CUSTOMER_API,
  SERVICE_REQUEST_API,
  TECHNICIAN_API,
} from '@shared/constants/api.constants';

import { environment } from '@environments/environment';

import { catchError, map } from 'rxjs/operators';
import {
  mapFormSearchParamToListQuery,
  mapHeldCompetencyToRow,
  mapProfileResponseToProfile,
} from '@shared/utilities/technician-api.adapter.util';
import { TECHNICIAN_AUDIT_MODULE } from '@shared/models/technician/technician.model';

import { Customer } from '@shared/models/customer/customer.model';
import { FormDetails, FormResponseDetails } from '@shared/models/form/form.model';
import { OfflineFormContextResponse } from '@shared/models/offline/offline-submission.model';

import {

  OfflineDownloadProgress,

  OfflineFormBundle,

  OfflineHttpCacheEntry,

  OfflineTechnicianWorkspace,

} from '@shared/models/offline/offline.model';

import {
  ServiceRequestDetails,
  ServiceRequestFormScheduledTechniciansResponse,
} from '@shared/models/service-request/service-request.model';

import {

  TechnicianFormRow,

  TechnicianProfile,
  TechnicianProfileResponse,
  TechnicianDashboardResponse,
  TechnicianFormSearchParam,
} from '@shared/models/technician/technician.model';

import { OfflinePolicyService } from '@shared/services/offline/offline-policy.service';

import { OfflineFormStateService } from '@shared/services/offline/offline-form-state.service';

import { OfflineStorageService } from '@shared/services/offline/offline-storage.service';

import {

  buildFormLocalStateKey,

  buildOfflineBundleId,

  buildOfflineCacheKey,

  buildTechnicianWorkspaceId,

  normalizeFormCode,

} from '@shared/utilities/offline-cache-key.util';

import {
  OFFLINE_SERVICE_FORM_ASSET_URLS,
  OFFLINE_SERVICE_ROUTE_IMPORTS,
} from '@shared/constants/offline-route-precache.constants';

import { OfflineSwPrecacheService } from '@shared/services/offline/offline-sw-precache.service';

import { firstValueFrom } from 'rxjs';
import {
  mapOfflineContextToPlacementDetail,
  mapPlacementToOfflineFormSnapshot,
} from '@features/service/pages/service-request-form-page/service-request-form-placement.util';
import { resolveServiceRequestFormDefinition } from '@features/service/constants/service-request-forms.constants';
import {
  isTechnicianScheduledOnServiceRequest,
  normalizeScheduledTechniciansResponse,
} from '@features/service/pages/service-request-form-page/service-request-form-scheduled-technicians.util';



const DEFAULT_TABLE_PAGE_SIZE = 10;



@Injectable({ providedIn: 'root' })

export class OfflineDownloadService {

  private readonly http = inject(HttpClient);



  constructor(

    private readonly policy: OfflinePolicyService,

    private readonly storage: OfflineStorageService,

    private readonly formState: OfflineFormStateService,

    private readonly swPrecache: OfflineSwPrecacheService,

  ) {}



  initRouteWarming(): void {

    if (!this.policy.isModuleEnabled('service')) {

      return;

    }



    void this.warmRoutesIfBundlesExist();

  }



  downloadForm(

    row: TechnicianFormRow,

    technicianId: number,

    clientId: string = environment.CLIENT_ID,

  ): Observable<OfflineDownloadProgress> {

    return from(this.executeDownload(row, technicianId, clientId)).pipe(

      catchError((error: Error) => of({

        stage: 'error' as const,

        percent: 0,

        message: error.message,

      })),

    );

  }



  repairOfflineForm(
    row: TechnicianFormRow,
    technicianId: number,
    clientId: string = environment.CLIENT_ID,
  ): Observable<OfflineDownloadProgress> {
    return this.downloadForm(row, technicianId, clientId);
  }

  async canClearFormOfflineData(
    row: TechnicianFormRow,
    clientId: string = environment.CLIENT_ID,
  ): Promise<boolean> {
    const bundle = await this.storage.findBundleByFormCode(
      clientId,
      row.serviceRequestId,
      row.formCode ?? '',
      row.formsId,
    );
    if (!bundle) {
      return false;
    }

    const formCode = row.formCode ?? bundle.formCode ?? '';
    if (!formCode) {
      return false;
    }

    if (await this.formState.hasUnsyncedLocal(clientId, row.serviceRequestId, formCode)) {
      return false;
    }

    const pendingMutations = await this.storage.listPendingMutations();
    const normalizedFormCode = normalizeFormCode(formCode);
    return !pendingMutations.some((mutation) =>
      mutation.clientId === clientId
      && mutation.serviceRequestId === row.serviceRequestId
      && (
        mutation.formPlacementId === row.formsId
        || normalizeFormCode(mutation.formCode) === normalizedFormCode
      ),
    );
  }

  async clearFormOfflineData(
    row: TechnicianFormRow,
    clientId: string = environment.CLIENT_ID,
  ): Promise<void> {
    const canClear = await this.canClearFormOfflineData(row, clientId);
    if (!canClear) {
      throw new Error(
        $localize`:@@offlineDataClearUnsyncedBlocked:Offline data cannot be cleared while this form has unsynced changes.`,
      );
    }

    const bundle = await this.storage.findBundleByFormCode(
      clientId,
      row.serviceRequestId,
      row.formCode ?? '',
      row.formsId,
    );
    if (!bundle) {
      return;
    }

    const formCode = row.formCode ?? bundle.formCode ?? '';
    if (formCode) {
      await this.storage.deleteFormLocalState(
        buildFormLocalStateKey(clientId, row.serviceRequestId, formCode),
      );
    }

    const otherBundles = (await this.storage.listBundles()).filter(
      (candidate) => candidate.bundleId !== bundle.bundleId,
    );
    const stillReferencedKeys = new Set(otherBundles.flatMap((candidate) => candidate.cacheKeyRefs));

    await Promise.all(
      bundle.cacheKeyRefs
        .filter((cacheKey) => !stillReferencedKeys.has(cacheKey))
        .map((cacheKey) => this.storage.deleteHttpCache(cacheKey)),
    );

    await this.storage.deleteBundle(bundle.bundleId);
  }



  listDownloadedBundles(): Observable<OfflineFormBundle[]> {

    return from(this.storage.listBundles());

  }



  async warmServiceRoutes(): Promise<void> {

    await Promise.all(OFFLINE_SERVICE_ROUTE_IMPORTS.map((load) => load()));

    await this.swPrecache.precacheOfflineServiceAssets();
    await this.swPrecache.precacheAssetUrls(OFFLINE_SERVICE_FORM_ASSET_URLS);

  }



  async prefetchTechnicianWorkspace(

    technicianId: number,

    clientId: string = environment.CLIENT_ID,

  ): Promise<void> {

    if (!this.policy.isModuleEnabled('service')) {

      return;

    }



    await this.warmServiceRoutes();



    const cacheKeyRefs: string[] = [];

    const now = new Date().toISOString();



    const profileHttpParams = { clientId, technicianId: String(technicianId) };
    const profileKey = buildOfflineCacheKey(clientId, 'TECHNICIAN_PROFILE', profileHttpParams);

    const profile = await firstValueFrom(
      this.http.get<TechnicianProfileResponse>(TECHNICIAN_API.PROFILE, { params: profileHttpParams }).pipe(
        map(mapProfileResponseToProfile),
      ),
    );

    cacheKeyRefs.push(profileKey);

    await this.storage.putHttpCache(this.toCacheEntry(

      profileKey, 'TECHNICIAN_PROFILE', TECHNICIAN_API.PROFILE, 'GET', profileHttpParams, profile, now,

    ));



    const countsHttpParams = { clientId, technicianId: String(technicianId) };

    const formsSearchParams: TechnicianFormSearchParam = {

      clientId,

      technicianId,

      pageNumber: 1,

      pageSize: DEFAULT_TABLE_PAGE_SIZE,

      sort: 'lastUpdate:desc',

      filter: { searchField: 'FORM_ID', searchParam: '' },

    };

    const formsQuery = mapFormSearchParamToListQuery(formsSearchParams);
    const formsHttpParams = Object.fromEntries(
      Object.entries(formsQuery).map(([key, value]) => [key, String(value)]),
    ) as Record<string, string>;

    const assignedSearchParams: Record<string, string> = {

      clientId,

      technicianId: String(technicianId),

      page: '1',

      pageSize: String(DEFAULT_TABLE_PAGE_SIZE),

    };

    const competenciesParams = {

      clientId,

      technicianId,

      pageNumber: 1,

      pageSize: DEFAULT_TABLE_PAGE_SIZE,

      sort: 'lastUpdated:desc',

      searchParam: '',

    };

    const updateLogsParams = {

      clientId,

      module: TECHNICIAN_AUDIT_MODULE,

      referenceId: technicianId,

      pageNumber: 1,

      pageSize: DEFAULT_TABLE_PAGE_SIZE,

      sort: 'dateCreated:desc',

    };

    const scheduleRange = this.buildSchedulePrefetchRange();

    const scheduleParams = {

      clientId,

      technicianId: String(technicianId),

      date: scheduleRange.start,

      view: 'week',

    };

    const [
      dashboardKey,
      formsSearchKey,
      assignedSearchKey,
      updateLogsSearchKey,
      scheduleEventsKey,
      competenciesSearchKey,
    ] = await Promise.all([
      this.cacheGet(
        clientId,
        'TECHNICIAN_DASHBOARD',
        TECHNICIAN_API.DASHBOARD,
        countsHttpParams,
        now,
      ),
      this.cacheGet(
        clientId, 'TECHNICIAN_FORMS_SEARCH', TECHNICIAN_API.FORMS, formsHttpParams, now,
      ),
      this.cacheGet(
        clientId,
        'TECHNICIAN_SCHEDULE',
        TECHNICIAN_API.SCHEDULE,
        assignedSearchParams,
        now,
      ),
      this.cachePost(
        clientId,
        'TECHNICIAN_UPDATE_LOGS_SEARCH',
        AUDIT_LOG_API.LIST,
        updateLogsParams,
        now,
      ),
      this.cacheGet(
        clientId,
        'TECHNICIAN_SCHEDULE_EVENTS',
        TECHNICIAN_API.CALENDAR,
        scheduleParams,
        now,
      ),
      this.cachePost(
        clientId,
        'TECHNICIAN_COMPETENCIES_SEARCH',
        TECHNICIAN_API.PROFILE,
        competenciesParams,
        now,
        of({
          recordsTotal: profile.competencies.length,
          recordsFiltered: profile.competencies.length,
          data: profile.competencies.map(mapHeldCompetencyToRow),
        }),
      ),
    ]);

    cacheKeyRefs.push(
      dashboardKey,
      formsSearchKey,
      assignedSearchKey,
      updateLogsSearchKey,
      scheduleEventsKey,
      competenciesSearchKey,
    );



    const workspace: OfflineTechnicianWorkspace = {

      workspaceId: buildTechnicianWorkspaceId(clientId, technicianId),

      clientId,

      technicianId,

      downloadedAt: now,

      cacheKeyRefs,

      defaultCacheKeys: {
        formsSearch: formsSearchKey,
        assignedRequestsSearch: assignedSearchKey,
        scheduleEvents: scheduleEventsKey,
        competenciesSearch: competenciesSearchKey,
        updateLogsSearch: updateLogsSearchKey,
      },

    };



    await this.storage.putTechnicianWorkspace(workspace);

  }



  private async assertTechnicianAssignedToServiceRequest(
    clientId: string,
    serviceRequestId: number,
    technicianId: number,
  ): Promise<void> {
    const response = await firstValueFrom(
      this.http.get<ServiceRequestFormScheduledTechniciansResponse>(
        SERVICE_REQUEST_API.SERVICE_REQUEST_FORM_SCHEDULED_TECHNICIANS,
        {
          params: {
            clientId,
            serviceRequestId: String(serviceRequestId),
          },
        },
      ),
    );
    const technicians = normalizeScheduledTechniciansResponse(response);
    if (!isTechnicianScheduledOnServiceRequest(technicians, technicianId)) {
      throw new Error(
        $localize`:@@offlineEnableRequiresAssignedTechnicianTooltip:Assign a technician to this service request to enable offline access.`,
      );
    }
  }

  private async warmRoutesIfBundlesExist(): Promise<void> {
    try {
      const [bundles, workspaces] = await Promise.all([
        this.storage.listBundles(),
        this.storage.listTechnicianWorkspaces(),
      ]);

      if (bundles.length > 0 || workspaces.length > 0) {
        await this.warmServiceRoutes();
      }
    } catch {
      // Route warming is best-effort; never block app bootstrap on refresh.
    }
  }



  private async prefetchFormOfflineHttpData(
    row: TechnicianFormRow,
    clientId: string,
    now: string,
  ): Promise<{
    cacheKeyRefs: string[];
    writtenCacheKeys: string[];
    offlineContext: OfflineFormContextResponse;
  }> {
    const cacheKeyRefs: string[] = [];
    const writtenCacheKeys: string[] = [];

    const srParams = { clientId, serviceRequestId: row.serviceRequestId };
    const offlineContextParams = {
      clientId,
      serviceRequestId: row.serviceRequestId,
      formPlacementId: row.formsId,
    };
    const scheduledTechniciansParams = {
      clientId,
      serviceRequestId: row.serviceRequestId,
    };

    const [
      serviceRequestEntry,
      offlineContextEntry,
      customerEntry,
      scheduledTechniciansEntry,
    ] = await Promise.all([
      this.fetchAndCacheGet<ServiceRequestDetails>(
        clientId,
        'SERVICE_REQUEST_DETAILS',
        SERVICE_REQUEST_API.SERVICE_REQUEST_DETAILS,
        srParams,
        now,
      ),
      this.fetchAndCacheGet<OfflineFormContextResponse>(
        clientId,
        'SERVICE_REQUEST_FORM_OFFLINE_CONTEXT',
        SERVICE_REQUEST_API.SERVICE_REQUEST_FORM_OFFLINE_CONTEXT,
        offlineContextParams,
        now,
      ),
      row.customerId
        ? this.fetchAndCacheGet<Customer>(
          clientId,
          'CUSTOMER_DETAILS',
          CUSTOMER_API.DETAILS,
          { clientId, customerId: String(row.customerId) },
          now,
        )
        : Promise.resolve(null),
      this.fetchAndCacheGetBestEffort<unknown>(
        clientId,
        'SERVICE_REQUEST_FORM_SCHEDULED_TECHNICIANS',
        SERVICE_REQUEST_API.SERVICE_REQUEST_FORM_SCHEDULED_TECHNICIANS,
        scheduledTechniciansParams,
        now,
      ),
    ]);

    cacheKeyRefs.push(serviceRequestEntry.cacheKey, offlineContextEntry.cacheKey);
    writtenCacheKeys.push(serviceRequestEntry.cacheKey, offlineContextEntry.cacheKey);

    if (customerEntry) {
      cacheKeyRefs.push(customerEntry.cacheKey);
      writtenCacheKeys.push(customerEntry.cacheKey);
    }

    if (scheduledTechniciansEntry) {
      cacheKeyRefs.push(scheduledTechniciansEntry.cacheKey);
      writtenCacheKeys.push(scheduledTechniciansEntry.cacheKey);
    }

    return {
      cacheKeyRefs,
      writtenCacheKeys,
      offlineContext: offlineContextEntry.body,
    };
  }

  private async fetchAndCacheGet<T>(
    clientId: string,
    endpointKey: OfflineHttpCacheEntry['endpointKey'],
    url: string,
    params: object,
    cachedAt: string,
  ): Promise<{ cacheKey: string; body: T }> {
    const cacheKey = buildOfflineCacheKey(clientId, endpointKey, params);
    const httpParams = Object.fromEntries(
      Object.entries(params).map(([key, value]) => [key, String(value)]),
    ) as Record<string, string>;
    const body = await firstValueFrom(this.http.get<T>(url, { params: httpParams }));
    await this.storage.putHttpCache(this.toCacheEntry(
      cacheKey, endpointKey, url, 'GET', params, body, cachedAt,
    ));
    return { cacheKey, body };
  }

  private async fetchAndCacheGetBestEffort<T>(
    clientId: string,
    endpointKey: OfflineHttpCacheEntry['endpointKey'],
    url: string,
    params: object,
    cachedAt: string,
  ): Promise<{ cacheKey: string; body: T } | null> {
    try {
      return await this.fetchAndCacheGet<T>(clientId, endpointKey, url, params, cachedAt);
    } catch {
      return null;
    }
  }

  private async executeDownload(

    row: TechnicianFormRow,

    technicianId: number,

    clientId: string,

  ): Promise<OfflineDownloadProgress> {

    if (!this.policy.isModuleEnabled('service')) {

      throw new Error('Offline mode is not enabled for service module');

    }

    await this.assertTechnicianAssignedToServiceRequest(clientId, row.serviceRequestId, technicianId);

    const now = new Date().toISOString();

    const existingBundle = await this.storage.findBundleByFormCode(
      clientId,
      row.serviceRequestId,
      row.formCode ?? '',
      row.formsId,
    );

    let writtenCacheKeys: string[] = [];

    try {

    const formPrefetch = await Promise.all([
      this.prefetchFormOfflineHttpData(row, clientId, now),
      this.prefetchTechnicianWorkspace(technicianId, clientId),
    ]).then(([prefetch]) => prefetch);

    writtenCacheKeys = formPrefetch.writtenCacheKeys;
    const { cacheKeyRefs, offlineContext } = formPrefetch;



    const resolvedFormCode = row.formCode ?? '';
    const placement = mapOfflineContextToPlacementDetail(offlineContext, resolvedFormCode);
    const isStaticForm = !!resolveServiceRequestFormDefinition(resolvedFormCode, offlineContext.formId)?.component;
    const { snapshot: downloadedForm, mode: downloadMode } = mapPlacementToOfflineFormSnapshot(
      placement,
      isStaticForm,
    );

    await this.formState.seedDownloadedForm(
      clientId,
      row.serviceRequestId,
      resolvedFormCode,
      row.formId,
      downloadedForm,
      downloadMode,
    );



    const bundle: OfflineFormBundle = {

      bundleId: buildOfflineBundleId(clientId, technicianId, row.formsId),

      clientId,

      technicianId,

      formsId: row.formsId,

      serviceRequestId: row.serviceRequestId,

      formCode: row.formCode ?? downloadedForm.formCode,

      formId: row.formId,

      customerId: row.customerId,

      downloadedAt: now,

      cacheKeyRefs,

    };



    await this.storage.putBundle(bundle);



    return { stage: 'complete', percent: 100 };

    } catch (error) {
      await this.rollbackWrittenCacheKeys(writtenCacheKeys);
      if (existingBundle) {
        await this.storage.putBundle(existingBundle);
      }
      throw error instanceof Error ? error : new Error('Offline download failed');
    }

  }



  private async rollbackWrittenCacheKeys(cacheKeys: string[]): Promise<void> {
    await Promise.all(cacheKeys.map((cacheKey) => this.storage.deleteHttpCache(cacheKey)));
  }



  private async cachePost(

    clientId: string,

    endpointKey: OfflineHttpCacheEntry['endpointKey'],

    url: string,

    params: object,

    cachedAt: string,

    request$?: Observable<unknown>,

  ): Promise<string> {

    const cacheKey = buildOfflineCacheKey(clientId, endpointKey, params);

    const body = await firstValueFrom(request$ ?? this.http.post<unknown>(url, params));

    await this.storage.putHttpCache(this.toCacheEntry(cacheKey, endpointKey, url, 'POST', params, body, cachedAt));

    return cacheKey;

  }



  private async cacheGet(

    clientId: string,

    endpointKey: OfflineHttpCacheEntry['endpointKey'],

    url: string,

    params: Record<string, string>,

    cachedAt: string,

    request$?: Observable<unknown>,

  ): Promise<string> {

    const cacheKey = buildOfflineCacheKey(clientId, endpointKey, params);

    const body = await firstValueFrom(request$ ?? this.http.get<unknown>(url, { params }));

    await this.storage.putHttpCache(this.toCacheEntry(cacheKey, endpointKey, url, 'GET', params, body, cachedAt));

    return cacheKey;

  }





  private buildSchedulePrefetchRange(): { start: string; end: string } {

    const start = new Date();

    start.setDate(start.getDate() - 30);

    const end = new Date();

    end.setDate(end.getDate() + 60);

    return {

      start: start.toISOString().split('T')[0],

      end: end.toISOString().split('T')[0],

    };

  }



  private toCacheEntry(

    cacheKey: string,

    endpointKey: OfflineHttpCacheEntry['endpointKey'],

    url: string,

    method: string,

    _params: object,

    body: unknown,

    cachedAt: string,

  ): OfflineHttpCacheEntry {

    return {

      cacheKey,

      endpointKey,

      url,

      method,

      body,

      status: 200,

      cachedAt,

    };

  }

}


